<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  useLookupFetcher,
  FilterDefinition,
  FilterType,
  FilterTypeEnum,
  useApplyFilter,
  formatOptionLabel
} from "@/features/filters";
import { useFiltersStore } from "@/store";
import { McSelect, McOption, McTooltip } from "@maersk-global/mds-components-core";
import { normalisedText } from "@/global";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
  disabled?: boolean;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  hiddenlabel,
  placeholder = "",
  groupLabel,
  groupId,
  sectionLabel,
  options: optionsProp,
  lookupUrl,
  config,
  dependencies,
  labelTooltip = false,
  clearButton,
  inputParams
} = props.filter;

const { t, locale } = useI18n();

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const selectedFilters = computed(() => {
  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = source?.[zone]?.[filterKey];
  return {
    ...(storeValue || { label: "", value: "" }),
    filterType: props.filterType
  };
});

const hasStaticOptions = !!optionsProp;
const hasLoaded = ref(false);

const resolvedDependencies = computed(() => {
  const deps: Record<string, any> = {};
  if (!dependencies) return deps;

  const zone = props.zonePrefix || "default";
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;

  for (const [param, dependsOn] of Object.entries(dependencies)) {
    const dep = source?.[zone]?.[dependsOn];
    deps[param] = typeof dep === "object" ? dep?.value : dep;
  }
  return deps;
});

const {
  options: fetchedOptions,
  fetch,
  loading
} = useLookupFetcher(
  filterKey,
  lookupUrl || "",
  config || {},
  { forceRefresh: false, autoFetchEnabled: false },
  resolvedDependencies,
  inputParams
);

const options = computed(() => {
  const rawOptions = optionsProp || fetchedOptions.value;
  const formatTemplate = props.filter.optionLabelFormat;
  const normaliseText = props.filter.normaliseText;
  const subLabelKey = props.filter.subLabelKey;

  if (!formatTemplate && !normaliseText)
    return rawOptions.map((opt) => ({ ...opt, ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}) }));

  return rawOptions.map((opt) => ({
    ...opt,
    ...(subLabelKey ? { sublabel: opt[subLabelKey] } : {}),
    ...(formatTemplate && { label: formatOptionLabel(opt, formatTemplate) || opt.label }),
    ...(normaliseText && { label: normalisedText(opt.label) || opt.label })
  }));
});

const onOpen = () => {
  if (!hasStaticOptions && !hasLoaded.value) {
    fetch();
    hasLoaded.value = true;
  }
};

watch(resolvedDependencies, () => {
  hasLoaded.value = false;
  const current = selectedFilters.value?.value;
  if (current) {
    const valid = options.value.some((option) => option.value === current);
    if (!valid) {
      removeFilter(filterKey);
    }
  }
});

const onChange = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value;
  const selectedOption = options.value.find((option) => option.value === value);
  const label = selectedOption?.label || value;

  applyFilter(filterKey, {
    filterKey,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {}),
    filterType: props.filterType,
    label,
    value
  });
};

const onClear = () => {
  removeFilter(filterKey);
};
</script>

<template>
  <div :style="{ width: props.filter.width, minWidth: props.filter.width }">
    <mc-tooltip fit="small">
      <mc-select
        :key="locale"
        slot="trigger"
        :name="filterKey"
        fit="small"
        :label="t(filterLabel)"
        :hiddenlabel="hiddenlabel"
        :placeholder="t(placeholder)"
        :loading="!hasStaticOptions && loading"
        :value="selectedFilters?.value"
        :clearbutton="clearButton"
        :disabled="props.disabled || undefined"
        @input="onChange"
        @focus="onOpen"
        @click="onOpen"
        @clearbuttonclick="onClear"
      >
        <mc-option v-for="option in options" :key="option.value" :value="option.value">
          {{ t(option.label) }}
        </mc-option>
      </mc-select>
      <span v-if="labelTooltip && selectedFilters?.value">{{ t(filterLabel) }}</span>
    </mc-tooltip>
  </div>
</template>
